import React, { useRef } from 'react';
import { ChefHat, Smartphone, BarChart3, ArrowRight, PlayCircle } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';

const heroBackground = `${process.env.PUBLIC_URL}/hero-scroll/upscayl_png_ultrasharp-4x_4x/ezgif-frame-001.png`;

const quickPoints = [
  { title: 'Pedidos em tempo real', icon: Smartphone },
  { title: 'Reservas organizadas', icon: ChefHat },
  { title: 'Painel completo', icon: BarChart3 },
];

interface HeroProps {
  onOpenDemo: (title?: string) => void;
}

const Hero: React.FC<HeroProps> = ({ onOpenDemo }) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.7], [1, 0.9]);
  const y = useTransform(scrollYProgress, [0, 0.7], [0, 100]);

  return (
    <section ref={targetRef} className="relative min-h-[120vh] w-full overflow-hidden bg-background font-sans">
      <div className="sticky top-0 h-screen w-full">
        <div className="absolute inset-0">
          <img
            src={heroBackground}
            alt=""
            className="h-full w-full object-cover opacity-20 md:opacity-35"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/70 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/82 to-background/45" />
          <div className="absolute left-1/2 top-[-8rem] h-[26rem] w-[26rem] -translate-x-1/2 rounded-full bg-primary/12 blur-[130px] md:left-1/4 md:top-0 md:h-[50rem] md:w-[50rem] md:translate-x-0 md:blur-[160px]" />
        </div>

        <motion.div 
          style={{ opacity, scale, y }}
          className="relative z-10 flex h-full items-center px-4 pb-10 pt-28 sm:px-6 md:px-8 md:pt-32 lg:pt-36"
        >
          <div className="mx-auto w-full max-w-[1400px]">
            <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-8">
              <div className="flex max-w-3xl flex-col space-y-5 text-left">
                <div className="space-y-3">
                  <h1 className="text-[2.4rem] font-black leading-[0.92] tracking-tighter text-white drop-shadow-2xl sm:text-5xl lg:text-7xl uppercase">
                    Gestão inteligente para <br />
                    <span className="bg-gradient-to-r from-primary via-primary/95 to-primary-hover bg-clip-text text-transparent drop-shadow-xl">
                      restaurantes modernos
                    </span>
                  </h1>

                  <p className="max-w-2xl text-sm font-medium leading-relaxed text-white/88 drop-shadow-2xl sm:text-base md:text-lg lg:text-xl">
                    Sincronize sua operação com o padrão máximo de qualidade. Controle total, fluidez extrema e resultados excepcionais.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-2.5 pt-1 sm:grid-cols-3">
                  {quickPoints.map((item) => (
                    <div
                      key={item.title}
                      className="flex min-h-[92px] flex-col gap-2 rounded-2xl border border-white/15 bg-white/8 p-3.5 backdrop-blur-xl"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/30 shadow-lg">
                        <item.icon className="h-4.5 w-4.5 text-white" />
                      </div>
                      <span className="text-xs font-bold tracking-tight text-white">{item.title}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                  <button 
                    onClick={() => window.location.href = '/admin/login'}
                    className="group flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-2xl shadow-primary/40 transition-all hover:bg-primary-hover active:scale-95 sm:w-auto sm:px-7 sm:text-base"
                  >
                    Começar agora
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button 
                    onClick={() => onOpenDemo()}
                    className="flex min-h-12 w-full items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-bold text-white backdrop-blur-xl transition-all hover:bg-white/20 active:scale-95 sm:w-auto sm:px-7 sm:text-base"
                  >
                    <PlayCircle className="h-4 w-4 text-primary" />
                    Agendar demonstração
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
